import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, User } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { teamMembers } from "@/data/team";
import { HERO_BACKGROUNDS } from "@/data/projectImages";
import PageHero from "@/components/PageHero";

const Team = () => {
  const { t, lang } = useT();

  const overline = { de: "Unser Team", en: "Our team", fr: "Notre équipe", it: "Il nostro team" }[lang];
  const title = { de: "Die Menschen hinter SF-Maler", en: "The people behind SF-Maler", fr: "Les personnes derrière SF-Maler", it: "Le persone dietro SF-Maler" }[lang];
  const subtitle = {
    de: "Erfahrene Handwerker, die mit Leidenschaft und Schweizer Präzision arbeiten.",
    en: "Experienced craftsmen working with passion and Swiss precision.",
    fr: "Des artisans expérimentés au service du travail bien fait.",
    it: "Artigiani esperti che lavorano con passione e precisione svizzera.",
  }[lang];

  return (
    <div data-testid="team-page">
      <PageHero
        overline={overline}
        title={title}
        subtitle={subtitle}
        backgroundImage={HERO_BACKGROUNDS.team}
      />

      <section className="py-14 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* CEO featured first */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10 items-center mb-14 sm:mb-20 pb-14 sm:pb-20 border-b border-zinc-200"
            data-testid="team-ceo"
          >
            <div className="md:col-span-4">
              <div className="aspect-square overflow-hidden bg-zinc-100 border border-zinc-200 max-w-[280px] mx-auto md:mx-0 relative group">
                {teamMembers[0].photo ? (
                  <img src={teamMembers[0].photo} alt={teamMembers[0].name} className="w-full h-full object-cover img-zoom" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200">
                    <span className="font-display text-7xl font-bold text-red-600">{teamMembers[0].initials}</span>
                  </div>
                )}
                <div className="absolute bottom-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 tracking-widest uppercase">
                  CEO
                </div>
              </div>
            </div>
            <div className="md:col-span-8">
              <span className="text-overline">{teamMembers[0].role[lang]}</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tighter text-zinc-900">
                {teamMembers[0].name}
              </h2>
              <p className="mt-5 text-base text-zinc-600 leading-relaxed max-w-2xl">
                {teamMembers[0].bio[lang]}
              </p>
            </div>
          </motion.div>

          {/* Other team members - smaller grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
            {teamMembers.slice(1).map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group"
                data-testid={`team-member-${m.id}`}
              >
                <div className="aspect-square overflow-hidden bg-zinc-100 border border-zinc-200 mb-4 relative card-lift">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-full h-full object-cover img-zoom" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-200 group-hover:from-red-50 group-hover:to-zinc-100 transition-all">
                      <User size={48} className="text-zinc-400 group-hover:text-red-600 transition-colors" strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <h3 className="font-display font-bold text-base text-zinc-900">{m.name}</h3>
                <p className="text-xs text-red-600 font-semibold mt-1 uppercase tracking-wider">{m.role[lang]}</p>
                <p className="mt-2 text-xs text-zinc-600 leading-relaxed">{m.bio[lang]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tighter text-zinc-900">
            {t.home.cta_section_title}
          </h2>
          <p className="mt-5 text-base text-zinc-600 leading-relaxed max-w-2xl mx-auto">{t.home.cta_section_text}</p>
          <div className="mt-8">
            <Link to="/kontakt" className="btn-primary btn-glow" data-testid="team-cta">
              {t.common.cta_contact} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
